import useSafeState from "../utils/safe-state";
import {Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Spin} from "antd";
import React, {useEffect} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import moment from "moment";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const OrderDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);
    const [itemLoading, setItemLoading] = useSafeState(false);
    const [supplierOptions, setSupplierOptions] = useSafeState<any[]>([]);
    const [itemOptions, setItemOptions] = useSafeState<any[]>([]);
    const [userSupplierCode, setUserSupplierCode] = useSafeState<any>();

    const handleEdit = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            
            const values = form.getFieldsValue();
            const formattedValues = {
                id: data.id,
                orderNo: values.orderNo,
                orderDate: values.orderDate ? moment(values.orderDate).format('YYYY-MM-DD') : null,
                deliveryDate: values.deliveryDate ? moment(values.deliveryDate).format('YYYY-MM-DD') : null,
                itemNo: values.itemNo,
                itemName: values.itemName,
                tarricCode: values.tarricCode,
                supplierCode: values.supplierCode,
                qty: values.qty,
                isSpareParts: values.isSpareParts || false,
                source: values.source
            };

            axiosInstance.current
                .put(
                    `/resource/orders/update?orderNo=${encodeURIComponent(data.orderNo)}&supplierCode=${encodeURIComponent(data.supplierCode)}&source=${encodeURIComponent(data.source)}`,
                    formattedValues,
                    config,
                )
                .then(() => {
                    openNotification('success', '');
                    props.onClose && props.onClose();
                })
                .catch(function (error: any) {
                    openNotification('error', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    useEffect(() => {

        const supplierCode = localStorage.getItem('supplierCode');
        setUserSupplierCode(supplierCode);
        form.resetFields();

        if(!supplierCode || supplierCode === 'undefined' || supplierCode === 'null'){
            getSuppliers();
        }

        if(props.mode === 'Edit') {
            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if(key === 'orderDate' || key === 'deliveryDate') {
                    fieldsValues.push({name: key, value: value ? moment(value as string) : null});
                } else {
                    fieldsValues.push({name: key, value: value});
                }
            }
            form.setFields(fieldsValues);
            // If editing, load items for the supplier
            const currentSupplierCode = data.supplierCode || ((userSupplierCode && userSupplierCode !== 'undefined' && userSupplierCode !== 'null') ? userSupplierCode : null);
            if (currentSupplierCode) {
                getItemsBySupplier(currentSupplierCode);
            }
        }

    }, []);

    const getSuppliers = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/suppliers')
                .then(response => {
                    const suppliers = Array.isArray(response.data) ? response.data : [];
                    setSupplierOptions(suppliers);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const getItemsBySupplier = (supplierCode: string) => {
        if (axiosInstance.current != null && supplierCode) {
            setItemLoading(true);
            axiosInstance.current
                .post('/resource/items/page', {
                    start: 0,
                    length: 10000,
                    draw: 1,
                    direction: 'asc',
                    sorted_field: 'itemNo',
                    query: {
                        supplierCode: supplierCode
                    }
                }, config)
                .then(response => {
                    const items = response.data && response.data.data ? response.data.data : [];
                    setItemOptions(items);
                })
                .catch(function (error: any) {
                    console.error('Error loading items:', error.response && error.response.data ? error.response.data : error);
                    openNotification('error', 'Error loading items for supplier');
                })
                .finally(() => {
                    setItemLoading(false);
                });
        }
    };

    const handleSupplierChange = (value: string) => {
        // Clear item selection when supplier changes
        form.setFieldsValue({ itemNo: undefined });
        setItemOptions([]);
        if (value) {
            getItemsBySupplier(value);
        }
    };

    const handleFinish = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            
            const values = form.getFieldsValue();
            const formattedValues = {
                orderNo: values.orderNo,
                orderDate: values.orderDate ? moment(values.orderDate).format('YYYY-MM-DD') : null,
                deliveryDate: values.deliveryDate ? moment(values.deliveryDate).format('YYYY-MM-DD') : null,
                itemNo: values.itemNo,
                itemName: values.itemName,
                tarricCode: values.tarricCode,
                supplierCode: (userSupplierCode && userSupplierCode !== 'undefined') ? userSupplierCode : values.supplierCode,
                qty: values.qty,
                isSpareParts: values.isSpareParts || false,
                source: values.source || 'MANUAL'
            };
            
            console.log('Sending order data:', formattedValues);

            axiosInstance.current
                .post(
                    '/resource/orders/create',
                    formattedValues,
                    config,
                )
                .then(() => {
                    openNotification('success', '');
                    props.onClose && props.onClose();
                })
                .catch(function (error: any) {
                    console.error('Order creation error:', error.response && error.response.data ? error.response.data : error);
                    openNotification('error', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    const onReset = () => {
        form.resetFields();
    };

    return (
        <Form
            id="newOrder"
            layout='vertical'
            form={form}
            onFinish={() => (props.mode === 'Edit' ? handleEdit() : handleFinish())}
        >
            <Spin spinning={loading}>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="orderNo"
                            label={<Trans>Order No</Trans>}
                            rules={[{required: true, message: <Trans>Please enter the order number</Trans>}]}
                        >
                            <Input disabled={props.mode === 'Edit'}/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="source"
                            label={<Trans>Source</Trans>}
                            rules={[{required: true, message: <Trans>Please enter the source</Trans>}]}
                        >
                            <Input placeholder="MANUAL" disabled={props.mode === 'Edit'}/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="orderDate"
                            label={<Trans>Order Date</Trans>}
                            rules={[{required: true, message: <Trans>Please select the order date</Trans>}]}
                        >
                            <DatePicker style={{width: '100%'}} format="YYYY-MM-DD"/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="deliveryDate"
                            label={<Trans>Delivery Date</Trans>}
                            rules={[{required: true, message: <Trans>Please select the delivery date</Trans>}]}
                        >
                            <DatePicker style={{width: '100%'}} format="YYYY-MM-DD"/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="supplierCode"
                            label={<Trans>Supplier</Trans>}
                            hidden={userSupplierCode && userSupplierCode !== 'undefined' && userSupplierCode !== 'null'}
                            rules={[{
                                required: !userSupplierCode || userSupplierCode === 'undefined' || userSupplierCode === 'null',
                                message: <Trans>Please select the supplier</Trans>
                            }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select supplier</Trans>}
                                optionFilterProp="children"
                                disabled={props.mode === 'Edit'}
                                onChange={handleSupplierChange}
                            >
                                {supplierOptions.map(supplier => (
                                    <Select.Option key={supplier.code} value={supplier.code}>
                                        {supplier.code} - {supplier.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="itemNo"
                            label={<Trans>MSC Part Number</Trans>}
                            rules={[{required: true, message: <Trans>Please select an item</Trans>}]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select item</Trans>}
                                optionFilterProp="children"
                                loading={itemLoading}
                                onChange={(value) => {
                                    const selectedItem = itemOptions.find(item => item.itemNo === value);
                                    if (selectedItem) {
                                        form.setFieldsValue({
                                            itemName: selectedItem.name,
                                            tarricCode: selectedItem.tarricCode
                                        });
                                    }
                                }}
                            >
                                {itemOptions.map(item => (
                                    <Select.Option key={item.itemNo} value={item.itemNo}>
                                        {item.itemNo} - {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="itemName"
                            label={<Trans>Item Name</Trans>}
                        >
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="tarricCode"
                            label={<Trans>Tarric Code / CN Code</Trans>}
                        >
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="qty"
                            label={<Trans>Quantity</Trans>}
                            rules={[{required: true, message: <Trans>Please enter the quantity</Trans>}]}
                        >
                            <InputNumber min={1} style={{width: '100%'}}/>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="isSpareParts"
                            valuePropName="checked"
                        >
                            <Checkbox><Trans>Is Spare Parts</Trans></Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    right: 0,
                    width: 720,
                    background: "#fff",
                    borderTop: "1px solid #e8e8e8",
                    padding: "10px 20px",
                    zIndex: 100,
                    textAlign: "right"
                }}
            >
                {props.mode === 'Edit'
                    ? (<Button htmlType="button" type="primary" onClick={() => form.submit()}>
                        <Trans>Save</Trans>
                    </Button>)
                    :
                    <>
                        <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                            <Trans>Clear</Trans>
                        </Button>
                        <Button
                            loading={loading}
                            htmlType="button"
                            type="primary"
                            style={{ marginRight: 8 }}
                            onClick={() => form.submit()}
                        >
                            <Trans>Save</Trans>
                        </Button>
                    </>
                }
            </div>
        </Form>
    );

}
export default OrderDrawer;
